import { CONSULT_INTENT_EVENT, isConsultIntentKind } from "./consultIntent";
import { DRAFT_READY_BAR_MS } from "./draftLatency";
import { GHOST_AFTER_MS } from "./ghostLabel";
import { buildLeadCard, type LeadCard, type LeadCardMemoryRow } from "./leadCard";
import type { ClaimScreen } from "./memorySaveGate";
import {
  NEXT_OCCASION_REACH_DAYS,
  isoDay,
  nextTouch,
  planOccasions,
  seasonalWindows,
  shopCalendarDay,
  shopDayEnd,
  type HeldKeyDate,
  type OccasionAppointmentFact,
  type OccasionFacts,
  type OccasionLostFact,
  type OccasionMemoryRow,
  type OccasionSaleFact,
  type PlannedTouch,
  type SeasonalWindow,
  type UnplannedFact,
} from "./occasionPlanner";
import { SCENARIO_MAP, type Scenario } from "./scenario";
import {
  EVIDENCE_WEIGHT,
  type AppointmentStatus,
  type Channel,
  type EvidenceKind,
  type InquiryType,
  type Outcome,
  TERMINAL_OUTCOMES,
} from "./types";
import { DAY_MS, HOURS, STANDARD_WINDOW_MS, evaluateWindowGate, type WindowState } from "./window";

/**
 * Loop item L6: lead quality, the Shepherd's stage, and an expected-value
 * call list. Agent Brains section 5.5 (Pipeline / Reactivation shepherd), L3
 * "call-list generation", and the thinking canvas's Shepherd boxes: "Stages
 * need exit criteria + SLA timers: New (respond <2 min) -> Engaged
 * (qualified <24h) -> Consult booked -> Deposit -> Delivered -> Reactivation
 * pool", "Rank by expected value: value x probability x urgency ... Never by
 * recency", "Every recommendation carries its why now in one line, the
 * reason must come from the record", and "a lead with no next step is a bug
 * you report".
 *
 * PURE AND READ ONLY. Plain records in, a ranked list out. No database, no
 * clock, no model call and no new inference: every stage, every BANT item and
 * every why-now line cites a row that already exists (a message id, an
 * appointment, an outcome, a `contact_memory` row, a `routing.decided`
 * event). Where the record has no source for something, the answer is "not
 * derivable" or "no evidence", never a guess (section 0). The loader in
 * `packages/db/src/callList.ts` is the only reader of real tables, and
 * `pnpm leads calllist` the only caller; nothing here sends, queues or
 * writes, and a route below is a label for the owner, never an intent.
 *
 * L10: a `consult.intent` event (the customer asked to come in, book or be
 * called) is cited on its lead as visit intent on record and read for
 * nothing else. It moves no stage and adds no urgency; `leadQuality.test.ts`
 * adds it and asserts an identical score and order, as for VIP below.
 *
 * F-11, T3: `vip_flag` and any wealth inference are NOT features. The
 * contact's VIP fields ride on `LeadRecord` because they are on the row the
 * loader reads, and no line of this file reads them; `leadQuality.test.ts`
 * flips them and asserts an identical list, score and order. Section 5.5's
 * own L4 line says "VIP carve-out respected", and a carve-out is a service
 * level decision this file does not take (see the L6 notes).
 *
 * WHY TA-181 COULD NOT BUILD THIS AND THIS CAN. TA-181 declined the call list
 * because no lead-value signal existed. None exists now either: every value
 * and probability weight below is 1 and AWAITING RATIFICATION, so today the
 * order is decided by urgency alone, which the record does carry. The tables
 * are where Kabir's numbers go; the formula and its citations are the part
 * that is buildable without them.
 */

/** The canvas's six Shepherd stages, in its order. */
export const SHEPHERD_STAGES = [
  "new",
  "engaged",
  "consult_booked",
  "deposit",
  "delivered",
  "reactivation_pool",
] as const;
export type ShepherdStage = (typeof SHEPHERD_STAGES)[number];

/**
 * A stage, or the honest answer that the record cannot say which. Never
 * defaulted to "new": a lead the record cannot place is counted and named in
 * the report, the canvas's "a lead with no next step is a bug you report".
 */
export type LeadStage = ShepherdStage | "not_derivable";

/**
 * Stages no record in this repository can evidence. Empty since V1 STAGES:
 * Delivered is read from an owner-recorded `order.delivered` event
 * (`deliveredStage`, written by `recordDelivery`), so every stage now has an
 * evidence source and the report header names none. A stage added here again
 * is named in that header, so its absence is visible rather than read as "no
 * lead is there".
 *
 * The Reactivation pool is not on this list: a ghosted outcome, an owner's tap
 * or the 21-day auto label (the loader reads `outcomes.source`, which words the
 * citation and the note and never moves the stage), places a lead
 * in it, and nothing else does (`ghostedStage`). RATIFIED by Kabir 2026-10-02 (decision list item 137, typed reply "Yes I want to continue the production" to the accept-the-recs question):
 * ghosted maps to reactivation_pool, delivered had no evidence source then
 * (V1 STAGES gave it one), and closed-window routing is unchanged.
 */
export const UNDERIVABLE_STAGES: readonly ShepherdStage[] = [];

/**
 * The canvas's stage SLA timers. T3 (section 5.5 L4: "SLA timers hard-coded
 * (T3)"), so this is a constant, never an input.
 *
 * New is the same 2 minutes F-06 rules for draft-ready latency, and is that
 * constant rather than a second spelling of it: while sending is human-gated,
 * F-06 says the live SLA is a gate-passed draft in under 2 minutes. What this
 * file can see is a shop or agent MESSAGE, not a ready draft, so New clears
 * only on a real reply. Engaged is the canvas's "qualified <24h", from the reply
 * to the latest customer message, so its clock restarts each exchange. No other
 * stage has a stated SLA, and null says so rather than inventing one.
 * RATIFIED by Kabir 2026-10-02 (decision list item 148, typed reply "Yes I want to continue the production" to the accept-the-recs question):
 * New clears on a message on record, not on a ready draft, and Engaged's 24h
 * clock restarts each exchange.
 */
export const STAGE_SLA_MS: Readonly<Record<ShepherdStage, number | null>> = {
  new: DRAFT_READY_BAR_MS,
  engaged: 24 * HOURS,
  consult_booked: null,
  deposit: null,
  delivered: null,
  reactivation_pool: null,
};

/**
 * Value of a lead by its routed scenario (`SCENARIO_MAP`'s names), plus
 * `unrouted` for a thread with no readable `routing.decided` event.
 *
 * AWAITING RATIFICATION. Every weight is 1 until Kabir sets them (section
 * 5.3 L5 makes score weights T1, outcome-tuned, which needs outcomes this
 * repository does not yet have in volume). The report header prints them as
 * provisional. Changing one here is the whole of Kabir's value decision.
 */
export const LEAD_VALUE_WEIGHTS: Readonly<Record<Scenario | "unrouted", number>> = {
  repair_inquiry: 1,
  ready_made_purchase: 1,
  custom_engagement: 1,
  price_challenge: 1,
  educated_buyer_debate: 1,
  complaint_warranty: 1,
  ghost_revival: 1,
  post_purchase_care: 1,
  consult_confirm: 1,
  review_ask: 1,
  vendor_spam: 1,
  fraud_suspect: 1,
  unknown: 1,
  unrouted: 1,
};

/**
 * Probability of a lead closing, by stage. AWAITING RATIFICATION, every
 * weight 1, for the same reason as `LEAD_VALUE_WEIGHTS`.
 */
export const LEAD_PROBABILITY_WEIGHTS: Readonly<Record<LeadStage, number>> = {
  new: 1,
  engaged: 1,
  consult_booked: 1,
  deposit: 1,
  delivered: 1,
  reactivation_pool: 1,
  not_derivable: 1,
};

/**
 * The urgency signals the record carries, each discrete. Discrete on
 * purpose: a continuous "hours until the window closes" would rank the lead
 * who spoke most recently last and the one who spoke longest ago first, and
 * that is recency ranking by another name (canvas: "Never by recency").
 *
 *  - window_open: a Meta conversation whose 24h window is open (in_window or
 *    grace); its closing time is cited.
 *  - window_grace: the final 2h of that window (F-01), when contact capture
 *    comes first.
 *  - sla_breached: the stage's SLA due time has arrived. "Under 2 min" is
 *    read strictly, as `draftLatency.ts` reads it, so due at exactly the bar.
 *  - occasion_within_30d: a dated occasion between now and 30 days ahead. Since
 *    loop item V8 the occasions are the planner's (`occasionPlanner.ts`): the
 *    contact's next proposed touch, from sale, deposit, appointment and
 *    key-date rows. A touch that spans days (a window) counts while any of its
 *    days is still ahead, so a window open today is within 30 days.
 */
export const URGENCY_SIGNALS = ["window_open", "window_grace", "sla_breached", "occasion_within_30d"] as const;
export type UrgencySignal = (typeof URGENCY_SIGNALS)[number];

/**
 * AWAITING RATIFICATION, every weight 1. Urgency is 1 plus the sum of the
 * weights of the signals present, so a lead with no signal is not zeroed out
 * of the list (a weight of 0 would hide it, and hiding is a decision).
 */
export const LEAD_URGENCY_WEIGHTS: Readonly<Record<UrgencySignal, number>> = {
  window_open: 1,
  window_grace: 1,
  sla_breached: 1,
  occasion_within_30d: 1,
};

/** The occasion horizon in the brief: a dated occasion within 30 days. */
export const OCCASION_HORIZON_MS = 30 * DAY_MS;

/**
 * Budget evidence: `dm.stated-budget` and every `payment.*` kind
 * `EVIDENCE_WEIGHT` prices, read from that table so a new payment kind joins
 * without a second list to update.
 */
export const BUDGET_EVIDENCE_KINDS: readonly EvidenceKind[] = (Object.keys(EVIDENCE_WEIGHT) as EvidenceKind[]).filter(
  (k) => k === "dm.stated-budget" || k.startsWith("payment."),
);

/**
 * The owner taps that evidence budget on their own: a deposit, one citation
 * in the evidence shape every other item uses (the tap's id and `outcomes.deposit`).
 * Its amount is never read: `LeadOutcomeRef` carries presence only, no figure
 * reaches a citation, and budget is reported and never scored or priced on
 * (T3, F-16, F-11). RATIFIED by Kabir 2026-10-02 (decision list item 148, typed reply "Yes I want to continue the production" to the accept-the-recs question):
 * a deposit tap counts as budget evidence.
 */
const BUDGET_EVIDENCE_OUTCOMES: readonly Outcome[] = ["deposit"];

/** The memory kinds that evidence a stage on their own. */
const DEPOSIT_FACT_KINDS: readonly EvidenceKind[] = ["payment.deposit-paid"];
const CONSULT_FACT_KINDS: readonly EvidenceKind[] = ["consult.attended"];
/** An appointment that is or was on the calendar. Cancelled and no-show are not a booked consult. */
const CONSULT_APPOINTMENT_STATUSES: readonly AppointmentStatus[] = ["booked", "confirmed", "attended"];
/** A date on the shop's calendar for this lead, not cancelled or missed: the timeline's stated date. */
const STATED_DATE_APPOINTMENT_STATUSES: readonly AppointmentStatus[] = ["booked", "confirmed"];
/**
 * Routed scenarios that evidence no customer's need, so BANT need never
 * cites them: a vendor's pitch, a fraud suspect, and a message the router
 * could not place. Every other scenario, class D complaints included, is a
 * customer asking for something. The list is this file's reading of section
 * 9's rows. RATIFIED by Kabir 2026-10-02 (decision list item 148, typed reply "Yes I want to continue the production" to the accept-the-recs question):
 * vendor_spam, fraud_suspect and unknown never evidence need.
 */
const NOT_A_CUSTOMER_NEED: readonly Scenario[] = ["vendor_spam", "fraud_suspect", "unknown"];

/**
 * The outcome labels that end what was recorded before them: a sale made, a
 * lead lost, a lead gone quiet. The latest one decides the lead when it is
 * the latest outcome of all; a deposit or booked tap after it starts over,
 * and nothing before it counts toward the stage. That boundary is this
 * file's reading, not the spec's. RATIFIED by Kabir 2026-10-02 (decision list item 148, typed reply "Yes I want to continue the production" to the accept-the-recs question):
 * sold, lost and ghosted end an episode. The three are `TERMINAL_OUTCOMES`
 * (types.ts), the one tuple the follow-up nudge's own terminal check reads too.
 */
const CLOSING_OUTCOMES: readonly Outcome[] = TERMINAL_OUTCOMES;

export interface LeadMessageRef {
  readonly messageId: string;
  readonly at: Date;
}

export interface LeadOutcomeRef {
  readonly outcomeId: string;
  readonly label: Outcome;
  readonly inquiryType: InquiryType | null;
  readonly recordedAt: Date;
  /**
   * V8: whether the outcome has an amount on record, presence only. The figure
   * is never selected by the loader and has no field here, so no amount can
   * reach a citation, a report or a log line (T3, F-16). Absent reads as false.
   */
  readonly amountOnRecord?: boolean;
  /**
   * Item SM part c (the LQ rec "owner tap vs 21-day auto label read
   * differently"): `outcomes.source` as stored ("owner_tap", "auto_scheduler",
   * "auto_ghost", "auto_payment"). It changes the WORDING of a ghosted
   * outcome's citation and stage note and nothing else: no stage, score, rank,
   * route or next action reads it. Absent, or a value this build does not name
   * for a ghosted outcome, keeps the text it always had.
   */
  readonly source?: string;
}

export interface LeadRoutingRef {
  readonly eventId: string;
  /** A string, not `Scenario`: the payload is jsonb, read unvalidated. */
  readonly scenario: string;
  readonly occurredAt: Date;
}

export interface LeadAppointmentRef {
  readonly appointmentId: string;
  readonly status: AppointmentStatus;
  readonly scheduledAt: Date;
}

/**
 * V1 STAGES: the latest `order.delivered` event on the thread, recorded by a
 * person (`recordDelivery`). Delivery is a physical fact, like an attendance:
 * it is never inferred from a message or a memory fact. Absent reads as null.
 */
export interface LeadDeliveryRef {
  readonly eventId: string;
  readonly at: Date;
}

/**
 * A dated occasion. No table records one, so the loader supplies none: since
 * loop item V8 the occasions a lead is read with are PLANNED from the record
 * (`occasionPlanner.ts`), and arrive here with a play name and the record ids
 * they cite. A plain ref (a test's, or a future source's) is a single instant.
 */
export interface LeadOccasionRef {
  readonly occasionId: string;
  readonly at: Date;
  /**
   * The last instant of a touch that spans days, for a planned window. The
   * occasion counts while `through` (or `at`, when absent) is not yet past.
   */
  readonly through?: Date;
  /** The planner's play name, for a planned occasion. */
  readonly play?: string;
  /** The record rows a planned occasion was made from. */
  readonly cites?: readonly LeadCitation[];
}

/**
 * L10: one `consult.intent` event on the thread, the worker's reading that a
 * customer message asked to come in, to book or to be called. `kind` is a
 * string, not `ConsultIntentKind`: the payload is jsonb, read unvalidated.
 * `occurredAt` is when the customer sent the message.
 */
export interface LeadConsultIntentRef {
  readonly eventId: string;
  readonly kind: string;
  readonly occurredAt: Date;
}

/** One open thread as the record has it. No message text: no field here can carry a body. */
export interface LeadRecord {
  readonly threadId: string;
  readonly contactId: string;
  /** The CUST token (section 10). */
  readonly contactPseudonym: string;
  readonly channel: Channel;
  readonly lastCustomerMessageAt: Date | null;
  /**
   * The thread's latest customer message. Threads are permanent, one per
   * client, contact and channel (`resolveThread.ts` upserts on that key), so
   * a returning customer writes into the thread they used months ago: New
   * and Engaged are read off the latest exchange, never the thread's first,
   * or a customer back after a year would be Engaged on last year's reply.
   */
  readonly latestInbound: LeadMessageRef | null;
  /**
   * The earliest customer message with no shop or agent message after it:
   * where the unanswered run starts, and New's clock. Null when the latest
   * customer message has been answered. A second message in the run never
   * restarts the clock.
   */
  readonly firstUnanswered: LeadMessageRef | null;
  /** The first shop or agent message at or after `latestInbound`: the reply to the current inbound, and Engaged's clock. */
  readonly replyToLatest: LeadMessageRef | null;
  /**
   * Every UNREVERSED outcome on the thread, in any order. Owner taps
   * accumulate (`recordOutcome` reverses only auto_ghost rows), so the
   * latest one alone would drop a deposit the moment a consult is tapped.
   */
  readonly outcomes: readonly LeadOutcomeRef[];
  /** The latest `routing.decided` event on the thread. */
  readonly latestRouting: LeadRoutingRef | null;
  /**
   * V8: the EARLIEST `routing.decided` event on the thread that routed
   * `custom_engagement` AND whose matched phrases include an engagement-ring
   * phrase (`routedAsEngagementRing`, the loader applies it), or null (absent
   * reads as null). The scenario alone is not enough: it also covers custom
   * pendants, wedding band sets and resets. "Earliest", not "latest": the latest
   * flips to another scenario after post-sale messages, and a ring sold on this
   * thread stays a ring. It marks a sale only when it is on record at or before
   * the sale and no owner-typed inquiry type says another kind of work
   * (`ringMarkerFor`; only `pnpm outcomes record --inquiry-type` records one,
   * sheet item 146). The occasion planner's only marker that a sale was
   * an engagement ring, and a weak one (see `occasionPlanner.ts`).
   */
  readonly engagementRouting?: LeadRoutingRef | null;
  readonly appointments: readonly LeadAppointmentRef[];
  /** V1 STAGES: the latest owner-recorded delivery on the thread, or null/absent. */
  readonly delivery?: LeadDeliveryRef | null;
  readonly occasions: readonly LeadOccasionRef[];
  /**
   * L10: every `consult.intent` event on the thread, in any order. Cited on
   * the lead as visit intent on record and read for nothing else: no stage,
   * no urgency, no weight (Kabir decides whether it ever is).
   */
  readonly consultIntents: readonly LeadConsultIntentRef[];
  /** The contact's `memory` layer rows, active and held, for `buildLeadCard`. */
  readonly memory: readonly LeadCardMemoryRow[];
  readonly contact: {
    readonly phoneOnFile: boolean;
    readonly emailOnFile: boolean;
    /** No consent column exists (F-20, TA-180); the loader passes false, as the escalation sweep does. */
    readonly consentRecorded: boolean;
    /** F-11: never read by this file. */
    readonly vipFlag: boolean;
    /** F-11: never read by this file. */
    readonly vipConfidence: number | null;
    /**
     * V8: a `send_suppressions` row exists for the contact (bereavement, an
     * opt-out, a sensitive life change: section 10.1), read as presence only,
     * never its reason. Absent reads as false. A suppressed contact is planned
     * no occasion at all (`occasionPlanner.ts`); nothing else here reads it.
     */
    readonly suppressed?: boolean;
  };
}

/**
 * One cited record item. `source` is the evidence kind for a memory row and
 * the table or event name otherwise; `note` is a fixed template with ids,
 * enum values and timestamps only, never a customer's or the shop's words.
 */
export interface LeadCitation {
  readonly kind: "thread" | "message" | "appointment" | "outcome" | "memory" | "event" | "occasion" | "suppression";
  readonly id: string;
  readonly source: string;
  readonly note: string;
}

export type StageReading =
  | {
      readonly open: true;
      readonly stage: LeadStage;
      readonly evidence: readonly LeadCitation[];
      readonly note: string;
      /** Lost is the latest outcome and the customer wrote after it: open again, stage not derivable. */
      readonly returnedAfterLost?: true;
    }
  | { readonly open: false; readonly evidence: readonly LeadCitation[]; readonly note: string };

export interface NextAction {
  readonly action: "respond" | "qualify" | "advance" | "set_stage";
  /** For `advance`: the next stage in the canvas order; null otherwise. */
  readonly toward: ShepherdStage | null;
  readonly dueAt: Date | null;
  readonly slaMs: number | null;
  readonly note: string;
}

export type BantStatus = "evidenced" | "held" | "no_evidence" | "not_applicable";

export interface BantComponent {
  readonly status: BantStatus;
  readonly items: readonly LeadCitation[];
  /** Section 4's contradiction holds of a matching kind: surfaced, never counted as evidence. */
  readonly held: readonly LeadCitation[];
  readonly note: string;
}

/** Adapted BANT. Reported, not scored: none of it enters the expected value. */
export interface LeadBant {
  readonly budget: BantComponent;
  readonly timeline: BantComponent;
  readonly need: BantComponent;
  readonly authority: BantComponent;
}

export interface UrgencyReading {
  readonly signals: readonly { readonly signal: UrgencySignal; readonly citations: readonly LeadCitation[] }[];
  readonly value: number;
}

export type LeadRoute = "reply_in_dm_now" | "consented_channel" | "owner_call" | "wait_for_trigger";

export interface RouteReading {
  readonly route: LeadRoute;
  readonly windowState: WindowState | "not_applicable";
  readonly closingSoon: boolean;
  readonly note: string;
}

export interface LeadAssessment {
  readonly threadId: string;
  readonly contactPseudonym: string;
  readonly channel: Channel;
  readonly stage: LeadStage;
  readonly stageEvidence: readonly LeadCitation[];
  readonly stageNote: string;
  readonly next: NextAction;
  readonly bant: LeadBant;
  readonly urgency: UrgencyReading;
  readonly value: { readonly key: Scenario | "unrouted"; readonly weight: number };
  readonly probability: { readonly key: LeadStage; readonly weight: number };
  readonly expectedValue: number;
  /** The citations behind the urgency signals, in signal order; null when there is no signal. */
  readonly whyNow: readonly LeadCitation[] | null;
  readonly route: RouteReading;
  /** Lost is recorded and the customer wrote after it: routed, never dropped, and named in the report. */
  readonly returnedAfterLost: boolean;
  /**
   * L10: visit intent on record, one citation per `consult.intent` event on
   * the thread (a visit, a consult or a call), by id; null when there is
   * none. SHOWN, NEVER RANKED: no line of this file reads it into the stage,
   * the urgency, a weight or the order. A customer saying "I'll stop by" is
   * not a booked consult (appointments.ts: words are text, not a booking), and
   * whether it should count toward Consult booked or as urgency is Kabir's.
   * Every intent on the thread is cited, one said before a closing outcome
   * too: the stage's boundary governs what counts toward a stage, and this
   * counts toward nothing.
   */
  readonly visitIntent: readonly LeadCitation[] | null;
  /**
   * The contact's memory rows `buildLeadCard` excluded as malformed and so did
   * not read as evidence (an unpriced kind, a fact with a confidence, an
   * inference without one): counted here so a dropped row is never silent.
   */
  readonly memoryExcludedMalformed: number;
}

export interface CallListRow extends LeadAssessment {
  readonly rank: number;
}

export interface CallList {
  readonly generatedAt: string;
  readonly rows: readonly CallListRow[];
  /**
   * Threads whose latest unreversed outcome is lost and whose customer has
   * not written since: not open, counted and named, never ranked.
   */
  readonly excludedLost: readonly { readonly threadId: string; readonly evidence: readonly LeadCitation[] }[];
  /** Threads lost and then written to by the customer: ranked and routed as not derivable, and named here. */
  readonly returnedAfterLost: readonly string[];
  readonly notDerivable: number;
  /** Rows whose next action has no due time: the canvas's "a lead with no next step is a bug you report". */
  readonly noDatedNextAction: number;
  /**
   * Memory rows excluded as malformed across every contact on the book, open
   * or lost, each contact counted once however many threads it has.
   */
  readonly memoryExcludedMalformed: number;  /**
   * Memory rows the recall screen quarantined (M1) across every contact on the
   * book, counted the same way: never read as evidence, and never silent.
   */
  readonly memoryQuarantined: number;
}

const iso = (d: Date): string => d.toISOString();

function isScenario(name: string): name is Scenario {
  return Object.prototype.hasOwnProperty.call(SCENARIO_MAP, name);
}

function messageCitation(ref: LeadMessageRef, source: string, note: string): LeadCitation {
  return { kind: "message", id: ref.messageId, source, note };
}

/**
 * How a ghosted outcome came to be, in words, or null when the record does not
 * say. The 21-day auto label (`sweepGhostLabels` writes `auto_ghost`) reads as
 * the silence it is, the number being the sweep's own `GHOST_AFTER_MS`; the
 * owner's tap reads as a tap. Only a ghosted outcome is worded this way, and
 * only the words differ (`LeadOutcomeRef.source`).
 *
 * RATIFIED by Kabir 2026-10-03 (item 21 of the 10-03 decision list, typed "all
 * recs i verify unelss it sacrifices proffesionalims depolyoemnt, and system
 * quality"; the rec, quoted whole: "SM part c: the wording "no reply for 21 days,
 * auto label" versus "tapped by the owner". Rec: confirm."). That settles the
 * two phrases, "no reply for 21 days, auto label" and "tapped by the owner", as
 * they stand in the outcome citation note and in the stage note. It does not
 * settle the closing words of the not_derivable notes ("the label stands", "the
 * tap stands", "may be a stale guess"): those sit in the branches of
 * `ghostedStage`, which keeps its own open marker, as do the other LQ readings.
 */
function ghostedHow(o: LeadOutcomeRef): string | null {
  if (o.label !== "ghosted") return null;
  if (o.source === "auto_ghost") return `no reply for ${Math.round(GHOST_AFTER_MS / DAY_MS)} days, auto label`;
  if (o.source === "owner_tap") return "tapped by the owner";
  return null;
}

function outcomeCitation(o: LeadOutcomeRef): LeadCitation {
  const how = ghostedHow(o);
  return {
    kind: "outcome",
    id: o.outcomeId,
    source: `outcomes.${o.label}`,
    note: `outcome ${o.label} recorded ${iso(o.recordedAt)}${how === null ? "" : `, ${how}`}`,
  };
}

function memoryCitation(f: { readonly memoryId: string; readonly evidenceKind: EvidenceKind; readonly writtenAt: string }, held = false): LeadCitation {
  return {
    kind: "memory",
    id: f.memoryId,
    source: f.evidenceKind,
    note: `${held ? "held " : ""}${f.evidenceKind} written ${f.writtenAt}`,
  };
}

function appointmentCitation(a: LeadAppointmentRef): LeadCitation {
  return { kind: "appointment", id: a.appointmentId, source: `appointments.${a.status}`, note: `appointment ${a.status}, scheduled ${iso(a.scheduledAt)}` };
}

/** Ids, never order of writing: a citation list must not rank by recency either. */
const byId = (a: LeadCitation, b: LeadCitation): number => a.id.localeCompare(b.id);

/**
 * L10. The kind only when it is one this build wrote: the note is a fixed
 * template of ids, enum values and timestamps, and an unread jsonb string is
 * none of those, so it is never echoed.
 */
function consultIntentCitation(i: LeadConsultIntentRef): LeadCitation {
  const kind = isConsultIntentKind(i.kind) ? i.kind : "kind not read";
  return { kind: "event", id: i.eventId, source: CONSULT_INTENT_EVENT, note: `${kind}, said ${iso(i.occurredAt)}` };
}

function visitIntentFor(record: LeadRecord): readonly LeadCitation[] | null {
  const cited = record.consultIntents.map(consultIntentCitation).sort(byId);
  return cited.length > 0 ? cited : null;
}

function activeFactsOfKind(card: LeadCard, kinds: readonly EvidenceKind[]): LeadCitation[] {
  return card.facts.filter((f) => kinds.includes(f.evidenceKind)).map((f) => memoryCitation(f)).sort(byId);
}

/**
 * M1's recall screen runs on every card this module reads (TA-507): a row the
 * screen quarantines is never evidence of a stage, a budget or an occasion.
 * Required, as on `buildLeadCard`, so a caller cannot rank on unscreened memory.
 */
function cardFor(record: LeadRecord, now: Date, screen: ClaimScreen): LeadCard {
  return buildLeadCard({ contactId: record.contactId, memory: record.memory, now, screen });
}

/**
 * What the occasion planner contributes to a lead's reading (V8): the contact's
 * next planned occasion as a ref the urgency and timeline rules already read,
 * and the key dates held by a contradiction, surfaced on the timeline and never
 * planned from.
 */
interface OccasionReading {
  readonly refs: readonly LeadOccasionRef[];
  readonly held: readonly LeadCitation[];
}

/**
 * The routing event that marks a sale or deposit as an engagement ring, or null.
 * Two conservative reads on top of the thread's earliest ring-phrase routing
 * (`engagementRouting`): it must be on record at or before the sale (a routing
 * after it is another conversation on a thread that is permanent per contact and
 * channel), and an owner tap that names another kind of work (a repair, a
 * ready-made piece) wins over the router's reading. THE VETO IS ARMED ONLY BY AN
 * OWNER-TYPED WORD (sheet item 146): `pnpm outcomes record --inquiry-type <type>`
 * writes `outcomes.inquiry_type`, and nothing else does (the inbox's chip and the
 * auto-ghost writer record none), so an outcome the owner did not type reads null
 * here, and a null leaves the marker standing. The safeguard that works for every
 * outcome is the positive marker itself, which the loader only hands over when
 * the routing matched an engagement-ring phrase.
 */
function ringMarkerFor(o: LeadOutcomeRef, marker: LeadRoutingRef | null): string | null {
  if (marker === null || marker.occurredAt.getTime() > o.recordedAt.getTime()) return null;
  if (o.inquiryType !== null && o.inquiryType !== "custom") return null;
  return marker.eventId;
}

/**
 * The planner's inputs from a contact's records (its threads) and its screened
 * card. Outcomes, appointments and the ring marker are per thread, so they are
 * gathered across the contact's threads; memory is per contact, and the card
 * is the one `cardFor` built, so a row the recall screen quarantined, or
 * `buildLeadCard` excluded, never reaches the planner. Only facts are read:
 * an inference is never a date.
 */
function occasionFactsFor(records: readonly LeadRecord[], card: LeadCard): OccasionFacts {
  const sales: OccasionSaleFact[] = [];
  const lost: OccasionLostFact[] = [];
  const appointments: OccasionAppointmentFact[] = [];
  for (const r of records) {
    const marker = r.engagementRouting ?? null;
    for (const o of r.outcomes) {
      if (o.label === "sold" || o.label === "deposit") {
        sales.push({ outcomeId: o.outcomeId, threadId: r.threadId, label: o.label, at: o.recordedAt, amountOnRecord: o.amountOnRecord === true, ringMarkerEventId: ringMarkerFor(o, marker) });
      } else if (o.label === "lost") {
        lost.push({ outcomeId: o.outcomeId, threadId: r.threadId, at: o.recordedAt });
      }
    }
    for (const a of r.appointments) appointments.push({ appointmentId: a.appointmentId, status: a.status, scheduledAt: a.scheduledAt });
  }
  const memory: OccasionMemoryRow[] = [
    ...card.facts.map((f) => ({ memoryId: f.memoryId, content: f.content, evidenceKind: f.evidenceKind, held: false })),
    ...card.heldContradictions
      .filter((h) => !h.isInference)
      .map((h) => ({ memoryId: h.memoryId, content: h.content, evidenceKind: h.evidenceKind, held: true })),
  ];
  const suppressed = records.some((r) => r.contact.suppressed === true);
  return { sales, lost, appointments, memory, suppressedContactId: suppressed ? (records[0]?.contactId ?? null) : null };
}

/** A touch as the ref the urgency and timeline rules read: its last day runs to the end of that UTC day. */
function refFromTouch(t: PlannedTouch): LeadOccasionRef {
  return { occasionId: t.id, at: t.from, through: new Date(t.through.getTime() + DAY_MS - 1), play: t.play, cites: t.cites };
}

/** The contact's next planned occasion, looking as far ahead as `NEXT_OCCASION_REACH_DAYS`: nearest first, one only. */
function plannedOccasionsFor(records: readonly LeadRecord[], card: LeadCard, now: Date): OccasionReading {
  const plan = planOccasions(occasionFactsFor(records, card), now, NEXT_OCCASION_REACH_DAYS);
  const next = nextTouch(plan);
  return { refs: next === null ? [] : [refFromTouch(next)], held: plan.held.flatMap((h) => h.cites) };
}

/**
 * An occasion as citations. A plain ref is one line; a planned one is the line
 * for the touch (play, days, provisional) and then every record row it was made
 * from, so a why-now or timeline item always names the ids behind the date.
 */
function occasionCitations(o: LeadOccasionRef, plain: string): LeadCitation[] {
  if (o.play === undefined) return [{ kind: "occasion", id: o.occasionId, source: "occasion", note: `${plain} ${iso(o.at)}` }];
  const last = o.through ?? o.at;
  return [
    { kind: "occasion", id: o.occasionId, source: `occasion.${o.play}`, note: `proposed ${o.play} ${isoDay(o.at)} to ${isoDay(last)}, provisional` },
    ...(o.cites ?? []),
  ];
}

const byOccasionId = (a: LeadOccasionRef, b: LeadOccasionRef): number => a.occasionId.localeCompare(b.occasionId);

/** Oldest first; one instant breaks by id, so the order never depends on how the rows arrived. */
const byRecordedThenId = (a: LeadOutcomeRef, b: LeadOutcomeRef): number =>
  a.recordedAt.getTime() - b.recordedAt.getTime() || a.outcomeId.localeCompare(b.outcomeId);

/**
 * The customer's latest message when it is STRICTLY after the outcome tap, or
 * null: a message at the same instant as the tap is not a customer coming back.
 */
function wroteAfter(record: LeadRecord, outcome: LeadOutcomeRef): LeadMessageRef | null {
  const back = record.latestInbound;
  return back !== null && back.at.getTime() > outcome.recordedAt.getTime() ? back : null;
}

/**
 * The stage of a lead whose latest outcome is ghosted: the Reactivation pool, or
 * not derivable. The mapping is item 137's, ratified on `UNDERIVABLE_STAGES`.
 * AWAITING RATIFICATION: LQ reading, item 137 is silent on a ghosted lead the
 * customer has written to since, or who has a message nobody answered. Both take
 * the lost rule, the most conservative reading: not_derivable, the tap and the
 * message cited, the owner sets the stage, never New or Engaged by default, and
 * no `returnedAfterLost` (that list says "lost"). Kabir to confirm or replace it.
 *
 *  - The customer wrote after the tap. An auto ghost is taken back the moment
 *    the customer writes (`reverseGhostLabel`), so a ghosted outcome still
 *    standing beside a later message is the owner's, which no code reverses.
 *  - A customer message has no shop reply after it (`unansweredSince`), though it
 *    is stamped at or before the tap. The ingest-lag shape (A6.4): the 21-day
 *    sweep wrote its auto ghost on a thread that looked silent, and the daily
 *    reconcile backfilled a message stamped before it, which `reverseGhostLabel`
 *    leaves standing. A customer is waiting inside an open window, so claiming
 *    "gone quiet" would be a false claim: precision beats recall (section 0).
 *    An owner who tapped ghosted over a message the record shows unanswered
 *    (the reply was off the record, a call) reads the same way, and sets the stage.
 *
 * Appointments are not read here, as for lost: `LeadAppointmentRef` carries no
 * time it was made, so one cannot be placed before or after the tap.
 */
function ghostedStage(record: LeadRecord, outcome: LeadOutcomeRef): StageReading {
  // Item SM part c: the words say how the label came to be. The branches and
  // the stages are the same for both sources; with no source the notes are the
  // sentences they always were. The 21-day auto label is not a tap, so for it
  // the label stands rather than the tap.
  const how = ghostedHow(outcome);
  const recorded = how === null ? "ghosted is recorded" : `ghosted is recorded (${how})`;
  const mark = outcome.source === "auto_ghost" ? "label" : "tap";
  const back = wroteAfter(record, outcome);
  if (back !== null) {
    return {
      open: true,
      stage: "not_derivable",
      evidence: [outcomeCitation(outcome), messageCitation(back, "messages.customer", `customer message ${iso(back.at)}, after the ghosted tap`)],
      note: `${recorded} and the customer wrote after it: the ${mark} stands, so the stage is the owner's to set`,
    };
  }
  const waiting = unansweredSince(record);
  if (waiting !== null) {
    return {
      open: true,
      stage: "not_derivable",
      evidence: [outcomeCitation(outcome), messageCitation(waiting, "messages.customer", `customer message ${iso(waiting.at)}, no reply after it`)],
      note: `${recorded} but a customer message has no reply after it: the ${mark} may be a stale guess, so the stage is the owner's to set`,
    };
  }
  return {
    open: true,
    stage: "reactivation_pool",
    evidence: [outcomeCitation(outcome)],
    note: `${recorded} and the customer has not written since: the Reactivation pool`,
  };
}

function deliveryCitation(d: LeadDeliveryRef): LeadCitation {
  return { kind: "event", id: d.eventId, source: "order.delivered", note: `delivery recorded ${iso(d.at)}` };
}

/**
 * V1 STAGES: Delivered, read from an owner-recorded delivery and nothing else.
 * AWAITING RATIFICATION: V1 reading, the design says only "teach stageFrom to
 * read the event". The conservative rule taken: the delivery must be recorded
 * at or after the latest standing tap that is not lost or ghosted (those are
 * read first and govern), so a later sale or deposit is a new order and not
 * this delivery. With no tap on record a delivery still places the lead, since
 * a person recorded a physical fact. Kabir to confirm or replace it.
 */
function deliveredStage(record: LeadRecord, latestTap: LeadOutcomeRef | null): StageReading | null {
  const d = record.delivery;
  if (d === undefined || d === null) return null;
  if (latestTap !== null && d.at.getTime() < latestTap.recordedAt.getTime()) return null;
  return {
    open: true,
    stage: "delivered",
    evidence: [...(latestTap !== null ? [outcomeCitation(latestTap)] : []), deliveryCitation(d)],
    note: "a delivery is on record",
  };
}

/**
 * The stage the record evidences, from the latest outcome and then the rest of
 * the record. RATIFIED by Kabir 2026-10-02 (decision list item 148, typed reply "Yes I want to continue the production" to the accept-the-recs question):
 * a customer writing after a lost tap is routed not_derivable and the owner
 * sets the stage; sold, lost and ghosted end an episode; a message at the same
 * instant as a lost tap is not a return; authority is not_applicable and the
 * timeline reads booked dates only. A ghosted latest outcome is read by
 * `ghostedStage`, and Delivered is read only from an owner-recorded delivery
 * (`deliveredStage`).
 */
function stageFrom(record: LeadRecord, card: LeadCard): StageReading {
  const outcomes = [...record.outcomes].sort(byRecordedThenId);
  const outcome = outcomes.at(-1);
  if (outcome !== undefined) {
    if (outcome.label === "lost") {
      // "Nobody rejected, everybody routed" (5.3). A customer who writes
      // after the owner's lost is back, and is routed. The tap still stands
      // (`reverseGhostLabel` never reverses a human's lost, and this does not
      // either), so the record cannot say which stage they are at: not
      // derivable, both rows cited, and the owner sets it.
      const back = wroteAfter(record, outcome);
      if (back !== null) {
        return {
          open: true,
          stage: "not_derivable",
          evidence: [outcomeCitation(outcome), messageCitation(back, "messages.customer", `customer message ${iso(back.at)}, after the lost tap`)],
          note: "lost is recorded and the customer wrote after it: the tap stands, so the stage is the owner's to set",
          returnedAfterLost: true,
        };
      }
      return { open: false, evidence: [outcomeCitation(outcome)], note: "lost is recorded and the customer has not written since: not an open lead" };
    }
    if (outcome.label === "sold") {
      const delivered = deliveredStage(record, outcome);
      if (delivered !== null) return delivered;
      return {
        open: true,
        stage: "not_derivable",
        evidence: [outcomeCitation(outcome)],
        note: "sold is recorded; Delivered has no evidence source, so the stage after a sale is not derivable",
      };
    }
    if (outcome.label === "ghosted") return ghostedStage(record, outcome);
  }

  // Only what was tapped after the latest sold, lost or ghosted counts: the
  // deposit of a sale already made is not the next piece's.
  const current = outcomes.slice(outcomes.findLastIndex((o) => CLOSING_OUTCOMES.includes(o.label)) + 1);
  const tapped = (label: Outcome): LeadCitation[] =>
    current.filter((o) => o.label === label).map(outcomeCitation).sort(byId);

  // The furthest stage the record evidences wins: a deposit after a consult
  // is a Deposit lead, whatever else is also true, whichever was tapped last.
  const deliveredNow = deliveredStage(record, current.at(-1) ?? null);
  if (deliveredNow !== null) return deliveredNow;

  const deposit = [...tapped("deposit"), ...activeFactsOfKind(card, DEPOSIT_FACT_KINDS)];
  if (deposit.length > 0) return { open: true, stage: "deposit", evidence: deposit, note: "a deposit is on record" };

  const consult = [
    ...tapped("booked"),
    ...record.appointments
      .filter((a) => CONSULT_APPOINTMENT_STATUSES.includes(a.status))
      .map(appointmentCitation)
      .sort(byId),
    ...activeFactsOfKind(card, CONSULT_FACT_KINDS),
  ];
  if (consult.length > 0) return { open: true, stage: "consult_booked", evidence: consult, note: "a consult is on record" };

  const latest = record.latestInbound;
  if (latest === null) {
    return { open: true, stage: "not_derivable", evidence: [], note: "no customer message on record, so neither New nor Engaged applies" };
  }
  const reply = record.replyToLatest;
  if (reply !== null && reply.at.getTime() >= latest.at.getTime()) {
    return {
      open: true,
      stage: "engaged",
      evidence: [messageCitation(reply, "messages.reply", `reply to the latest customer message ${iso(reply.at)}`)],
      note: "the shop has replied to the latest customer message",
    };
  }
  const unanswered = unansweredSince(record) ?? latest;
  return {
    open: true,
    stage: "new",
    evidence: [messageCitation(unanswered, "messages.customer", `first unanswered customer message ${iso(unanswered.at)}`)],
    note: "a customer message with no reply after it",
  };
}

/**
 * The customer message New's clock runs from, or null when there is none to
 * answer (no customer message, or the latest one has a reply at or after
 * it). The loader is not trusted on the order: a reply stamped before the
 * latest inbound is no reply to it, and a "first unanswered" stamped after
 * the latest inbound is not the start of the run, so the latest inbound
 * anchors instead.
 */
function unansweredSince(record: LeadRecord): LeadMessageRef | null {
  const latest = record.latestInbound;
  if (latest === null) return null;
  const reply = record.replyToLatest;
  if (reply !== null && reply.at.getTime() >= latest.at.getTime()) return null;
  const first = record.firstUnanswered;
  return first !== null && first.at.getTime() <= latest.at.getTime() ? first : latest;
}

/** The stage the record evidences, or `open: false` for a lead recorded as lost and not written to since. */
export function deriveStage(record: LeadRecord, now: Date, screen: ClaimScreen): StageReading {
  return stageFrom(record, cardFor(record, now, screen));
}

function nextStage(stage: ShepherdStage): ShepherdStage | null {
  const at = SHEPHERD_STAGES.indexOf(stage);
  return SHEPHERD_STAGES[at + 1] ?? null;
}

/**
 * The next action a stage owes: the owner sets a stage the record cannot place,
 * New and Engaged run their SLA clocks, and any other stage is `advance` toward
 * the next one with no due time.
 * AWAITING RATIFICATION: LQ reading, item 137 is silent on what a Reactivation
 * pool lead's next action is. It reads `advance` with no target, no due time and
 * no SLA, because the canvas states no SLA for the pool, it is the last stage,
 * and its play (V7) is parked. Kabir to confirm or replace it.
 */
function nextActionFor(record: LeadRecord, stage: LeadStage): { next: NextAction; anchor: LeadMessageRef | null } {
  if (stage === "not_derivable") {
    return {
      next: { action: "set_stage", toward: null, dueAt: null, slaMs: null, note: "stage not derivable: no SLA applies; the owner sets the stage" },
      anchor: null,
    };
  }
  const sla = STAGE_SLA_MS[stage];
  // New's clock starts at the first unanswered customer message, Engaged's
  // at the reply to the latest one: each is the moment the CURRENT exchange
  // entered the stage, on a thread that may hold years of earlier ones.
  const anchor = stage === "new" ? unansweredSince(record) : stage === "engaged" ? record.replyToLatest : null;
  if (sla !== null && anchor !== null) {
    return {
      next: {
        action: stage === "new" ? "respond" : "qualify",
        toward: null,
        dueAt: new Date(anchor.at.getTime() + sla),
        slaMs: sla,
        note: `${stage} SLA from ${iso(anchor.at)}`,
      },
      anchor,
    };
  }
  const toward = nextStage(stage);
  return {
    next: {
      action: "advance",
      toward,
      dueAt: null,
      slaMs: null,
      // The Reactivation pool ends the canvas order and its play (V7) is parked: say there is nothing to advance toward.
      note: toward === null ? `no SLA stated for ${stage}; it is the last stage, so there is nothing to advance toward` : `no SLA stated for ${stage}`,
    },
    anchor: null,
  };
}

function component(items: readonly LeadCitation[], held: readonly LeadCitation[], evidenced: string, none: string): BantComponent {
  if (items.length > 0) return { status: "evidenced", items, held, note: evidenced };
  if (held.length > 0) return { status: "held", items, held, note: "only held contradictions of this kind: surfaced, not counted" };
  return { status: "no_evidence", items, held, note: none };
}

/**
 * The four BANT components, each with the record items behind it.
 * AWAITING RATIFICATION: LQ reading, item 148 is silent on whether a deposit tap
 * recorded before a later sold, lost or ghosted tap still counts as budget
 * evidence. It does, with no episode scope, as a `payment.deposit-paid` memory
 * fact does (item 148 says old appointments and memory facts have no episode
 * scope), so a repeat buyer's earlier deposit stays one budget line. Kabir to
 * confirm, or to scope the tap by date with the appointments and memory.
 */
function bantFor(record: LeadRecord, card: LeadCard, occasion: OccasionReading): LeadBant {
  const budget = component(
    [
      ...activeFactsOfKind(card, BUDGET_EVIDENCE_KINDS),
      ...record.outcomes.filter((o) => BUDGET_EVIDENCE_OUTCOMES.includes(o.label)).map(outcomeCitation).sort(byId),
    ],
    card.heldContradictions
      .filter((h) => !h.isInference && BUDGET_EVIDENCE_KINDS.includes(h.evidenceKind))
      .map((h) => memoryCitation(h, true))
      .sort(byId),
    "stated budget, payment facts or a deposit tap",
    "no dm.stated-budget or payment fact and no deposit tap",
  );

  const timeline = component(
    [
      ...[...record.occasions, ...occasion.refs].sort(byOccasionId).flatMap((o) => occasionCitations(o, "dated occasion")),
      ...record.appointments
        .filter((a) => STATED_DATE_APPOINTMENT_STATUSES.includes(a.status))
        .map(appointmentCitation)
        .sort(byId),
    ],
    [...occasion.held].sort(byId),
    "a dated occasion or a stated date",
    "no dated occasion and no booked date on record",
  );

  const routing = record.latestRouting;
  const routed = routing !== null && isScenario(routing.scenario) ? routing.scenario : null;
  const need = component(
    [
      ...(routing !== null && routed !== null && !NOT_A_CUSTOMER_NEED.includes(routed)
        ? [{ kind: "event", id: routing.eventId, source: "routing.decided", note: `scenario ${routed}` } as const]
        : []),
      ...record.outcomes
        .filter((o) => o.inquiryType !== null && o.inquiryType !== "dead")
        .map((o): LeadCitation => ({ kind: "outcome", id: o.outcomeId, source: "outcomes.inquiry_type", note: `inquiry type ${o.inquiryType}` }))
        .sort(byId),
    ],
    [],
    "the routed scenario or the recorded inquiry type",
    routing !== null && routed === null
      ? "the latest routing.decided names a scenario this map does not know; not counted"
      : routed !== null && NOT_A_CUSTOMER_NEED.includes(routed)
        ? `the latest routing.decided names ${routed}, which evidences no customer's need; not counted`
        : "no routed scenario and no inquiry type on record",
  );

  return {
    budget,
    timeline,
    need,
    authority: { status: "not_applicable", items: [], held: [], note: "no gift-buyer signal is modeled" },
  };
}

function routeFor(record: LeadRecord, now: Date): RouteReading {
  const gate = evaluateWindowGate(
    { lastCustomerMessageAt: record.lastCustomerMessageAt, now },
    // `hasMarketingOptIn` false: a marketing message is the Campaign
    // Operator's one-to-many play, not a route this 1:1 list offers.
    { kind: "freeform", channel: record.channel, hasMarketingOptIn: false },
  );
  if (gate.reason === "in_window" || gate.reason === "grace_window_closing") {
    return {
      route: "reply_in_dm_now",
      windowState: gate.state,
      closingSoon: gate.closingSoon,
      note: gate.closingSoon ? "window in its final 2h grace: capture contact first (F-01)" : "window open",
    };
  }

  let windowState: RouteReading["windowState"];
  let why: string;
  if (gate.reason === "not_meta_channel_window_not_applicable") {
    windowState = "not_applicable";
    why = "not a Meta conversation: the window does not apply and F-20's consent regime is not specced, so no reply route is offered";
  } else if (gate.reason === "public_comment_window_not_applicable") {
    windowState = "not_applicable";
    why = "public comment: the window does not govern it (TA-48) and a comment is not a DM, so no DM route is offered";
  } else {
    windowState = "closed";
    why = "window closed: no free-form DM (F-02)";
  }

  const c = record.contact;
  if ((c.phoneOnFile || c.emailOnFile) && c.consentRecorded) {
    return { route: "consented_channel", windowState, closingSoon: false, note: `${why}; contact captured and consent recorded` };
  }
  if (c.phoneOnFile) {
    return { route: "owner_call", windowState, closingSoon: false, note: `${why}; phone on file, no consent recorded: the owner calls, nothing is sent` };
  }
  return { route: "wait_for_trigger", windowState, closingSoon: false, note: `${why}; no consented contact: wait for the customer to write` };
}

function urgencyFor(
  record: LeadRecord,
  route: RouteReading,
  next: NextAction,
  anchor: LeadMessageRef | null,
  stage: LeadStage,
  now: Date,
  occasions: readonly LeadOccasionRef[],
): UrgencyReading {
  const signals: { signal: UrgencySignal; citations: LeadCitation[] }[] = [];
  const last = record.lastCustomerMessageAt;
  if (route.route === "reply_in_dm_now" && last !== null) {
    const closesAt = new Date(last.getTime() + STANDARD_WINDOW_MS);
    signals.push({
      signal: "window_open",
      citations: [{ kind: "thread", id: record.threadId, source: "threads.last_customer_message_at", note: `window closes ${iso(closesAt)}` }],
    });
    if (route.windowState === "grace") {
      signals.push({
        signal: "window_grace",
        citations: [{ kind: "thread", id: record.threadId, source: "threads.last_customer_message_at", note: "final 2h grace (F-01)" }],
      });
    }
  }
  if (next.dueAt !== null && anchor !== null && now.getTime() >= next.dueAt.getTime()) {
    signals.push({
      signal: "sla_breached",
      citations: [messageCitation(anchor, stage === "new" ? "messages.customer" : "messages.reply", `${stage} SLA due ${iso(next.dueAt)}`)],
    });
  }
  const horizon = now.getTime() + OCCASION_HORIZON_MS;
  // Still ahead: a touch that spans days counts until its last day is past.
  const soon = occasions.filter((o) => (o.through ?? o.at).getTime() >= now.getTime() && o.at.getTime() <= horizon);
  if (soon.length > 0) {
    signals.push({
      signal: "occasion_within_30d",
      citations: [...soon].sort(byOccasionId).flatMap((o) => occasionCitations(o, "occasion")),
    });
  }
  const value = 1 + signals.reduce((sum, s) => sum + LEAD_URGENCY_WEIGHTS[s.signal], 0);
  return { signals, value };
}

function assessFrom(
  record: LeadRecord,
  card: LeadCard,
  reading: StageReading & { readonly open: true },
  now: Date,
  occasion: OccasionReading,
): LeadAssessment {
  const stage = reading.stage;
  const { next, anchor } = nextActionFor(record, stage);
  const route = routeFor(record, now);
  const urgency = urgencyFor(record, route, next, anchor, stage, now, [...record.occasions, ...occasion.refs]);

  const routed = record.latestRouting?.scenario;
  const valueKey: Scenario | "unrouted" = routed !== undefined && isScenario(routed) ? routed : "unrouted";
  const value = { key: valueKey, weight: LEAD_VALUE_WEIGHTS[valueKey] };
  const probability = { key: stage, weight: LEAD_PROBABILITY_WEIGHTS[stage] };
  const whyNow = urgency.signals.flatMap((s) => s.citations);

  return {
    threadId: record.threadId,
    contactPseudonym: record.contactPseudonym,
    channel: record.channel,
    stage,
    stageEvidence: reading.evidence,
    stageNote: reading.note,
    next,
    bant: bantFor(record, card, occasion),
    urgency,
    value,
    probability,
    expectedValue: value.weight * probability.weight * urgency.value,
    whyNow: whyNow.length > 0 ? whyNow : null,
    route,
    returnedAfterLost: reading.returnedAfterLost === true,
    visitIntent: visitIntentFor(record),
    memoryExcludedMalformed: card.excludedMalformed,
  };
}

/**
 * One open lead, assessed. Null when the record says the lead is not open
 * (lost, and the customer has not written since); `buildCallList` counts
 * those instead of ranking them.
 */
export function assessLead(record: LeadRecord, now: Date, screen: ClaimScreen): LeadAssessment | null {
  const card = cardFor(record, now, screen);
  const reading = stageFrom(record, card);
  return reading.open ? assessFrom(record, card, reading, now, plannedOccasionsFor([record], card, now)) : null;
}

/** A contact's rows, in thread order so nothing downstream depends on the order the loader returned them in. */
function groupByContact(leads: readonly LeadRecord[]): Map<string, LeadRecord[]> {
  const byContact = new Map<string, LeadRecord[]>();
  for (const record of leads) byContact.set(record.contactId, [...(byContact.get(record.contactId) ?? []), record]);
  for (const records of byContact.values()) records.sort((a, b) => a.threadId.localeCompare(b.threadId));
  return byContact;
}

/**
 * Every open lead, ranked by expected value, highest first; equal scores
 * break by thread id so two runs over the same record never disagree about
 * order (the `outcomeDigest.ts` discipline). Never capped: the caller
 * decides how many to print and says how many it did not.
 *
 * V8: an occasion belongs to the CONTACT, not the thread: a ring sold on one
 * thread plans bands for the person, whichever of their threads is on the
 * list. So the plan is made once per contact from all of its threads, open or
 * lost (a lost tap after a sale is one of the planner's own inputs), and each
 * open thread of the contact reads its next occasion.
 */
export function buildCallList(input: {
  readonly leads: readonly LeadRecord[];
  readonly now: Date;
  readonly screen: ClaimScreen;
}): CallList {
  const cards = new Map<LeadRecord, LeadCard>();
  for (const record of input.leads) cards.set(record, cardFor(record, input.now, input.screen));

  // Memory is per contact, so any of its rows' cards carries it; the lowest thread id's is the one read.
  const occasionByContact = new Map<string, OccasionReading>();
  for (const [contactId, records] of groupByContact(input.leads)) {
    const first = records[0];
    const card = first === undefined ? undefined : cards.get(first);
    if (first !== undefined && card !== undefined) occasionByContact.set(contactId, plannedOccasionsFor(records, card, input.now));
  }

  const assessed: LeadAssessment[] = [];
  const excludedLost: { threadId: string; evidence: readonly LeadCitation[] }[] = [];
  const malformedByContact = new Map<string, number>();
  const quarantinedByContact = new Map<string, number>();
  for (const record of input.leads) {
    const card = cards.get(record) ?? cardFor(record, input.now, input.screen);
    malformedByContact.set(record.contactId, card.excludedMalformed);
    quarantinedByContact.set(record.contactId, card.quarantined.length);
    const reading = stageFrom(record, card);
    if (reading.open) assessed.push(assessFrom(record, card, reading, input.now, occasionByContact.get(record.contactId) ?? { refs: [], held: [] }));
    else excludedLost.push({ threadId: record.threadId, evidence: reading.evidence });
  }
  assessed.sort((a, b) => b.expectedValue - a.expectedValue || a.threadId.localeCompare(b.threadId));
  excludedLost.sort((a, b) => a.threadId.localeCompare(b.threadId));
  return {
    generatedAt: iso(input.now),
    rows: assessed.map((a, i) => ({ ...a, rank: i + 1 })),
    excludedLost,
    returnedAfterLost: assessed.filter((a) => a.returnedAfterLost).map((a) => a.threadId).sort((a, b) => a.localeCompare(b)),
    notDerivable: assessed.filter((a) => a.stage === "not_derivable").length,
    noDatedNextAction: assessed.filter((a) => a.next.dueAt === null).length,
    memoryExcludedMalformed: [...malformedByContact.values()].reduce((sum, n) => sum + n, 0),
    memoryQuarantined: [...quarantinedByContact.values()].reduce((sum, n) => sum + n, 0),
  };
}

// ---- the occasion calendar (V8, behind `pnpm leads occasions`) ------------------

/** Most open first. Ties are the lowest thread id's, so two runs never disagree about which thread is named. */
const ROUTE_RANK: Readonly<Record<LeadRoute, number>> = {
  reply_in_dm_now: 0,
  consented_channel: 1,
  owner_call: 2,
  wait_for_trigger: 3,
};

/**
 * The route a contact reads at one instant: the most open of its threads'
 * routes, with that thread named, or null when it has no thread. The very
 * reading `assessLead` gives a thread (`routeFor`), asked at a chosen time, so
 * the calendar says what the call list would say on the day a touch is due and
 * never a second opinion on windows (T3: the window state machine is not this
 * file's to bend). A closed window routes to the owner's call or to waiting,
 * never a free-form DM (F-02).
 */
export function routeReadingAt(
  records: readonly LeadRecord[],
  at: Date,
): { readonly route: RouteReading; readonly threadId: string } | null {
  let best: { route: RouteReading; threadId: string } | null = null;
  for (const r of [...records].sort((a, b) => a.threadId.localeCompare(b.threadId))) {
    const route = routeFor(r, at);
    if (best === null || ROUTE_RANK[route.route] < ROUTE_RANK[best.route.route]) best = { route, threadId: r.threadId };
  }
  return best;
}

export interface OccasionCalendarRow {
  readonly contactPseudonym: string;
  readonly touch: PlannedTouch;
  /** The route as of `routeAt`: now for a touch already open, the end of its first day for one still ahead. */
  readonly route: RouteReading;
  readonly routeAt: Date;
  /** The contact's thread the route was read on. */
  readonly routeThreadId: string;
}

export type OccasionCalendarUnplanned = UnplannedFact & { readonly contactPseudonym: string };
export type OccasionCalendarHeld = HeldKeyDate & { readonly contactPseudonym: string };

export interface OccasionCalendar {
  readonly generatedAt: string;
  readonly days: number;
  /** Proposed touches that start inside the window or are open today, earliest first, ties by token. */
  readonly rows: readonly OccasionCalendarRow[];
  /** Facts that planned nothing (undated, no marker, no play, absorbed, lost after), named under the contact's token. */
  readonly unplanned: readonly OccasionCalendarUnplanned[];
  /** Key dates in section 4's contradiction hold: shown, never planned from. */
  readonly held: readonly OccasionCalendarHeld[];
  /** Contacts on the send suppression list: planned nothing, and named so the gap is never silent. */
  readonly suppressed: readonly { readonly contactPseudonym: string; readonly cites: readonly LeadCitation[]; readonly note: string }[];
  /** Contacts whose every thread is lost and silent since: not an open lead, so planned nothing, named by token. */
  readonly excludedLost: readonly string[];
  /** Seasonal waves in the window: a calendar of dates with no person in it. */
  readonly seasonal: readonly SeasonalWindow[];
  /** Contacts read (a contact counts once however many threads it has), and windows or marks already over. */
  readonly contacts: number;
  readonly elapsed: number;
  readonly memoryExcludedMalformed: number;
  readonly memoryQuarantined: number;
}

/**
 * The upcoming proposed touches across a book, for `pnpm leads occasions`.
 * Pure and read only, like the call list: the same screened card, the same
 * planner, and no wording, price or discount anywhere in what it returns. A
 * seasonal wave is a window on the calendar and never a row for a person.
 *
 * G19 (TA-1261): `shopTimeZone`, the shop's IANA zone (`clients.timezone`),
 * reaches the planner, so a key date or a sale is dated on the shop's
 * calendar, and an upcoming touch's route is read at the end of its first day
 * in that zone. Null or absent keeps UTC days, the reading before the column
 * existed. An unknown zone throws `ShopTimeZoneUnknown` before any lead is
 * read, never falls back to UTC (bad dates are worse than none).
 */
export function buildOccasionCalendar(input: {
  readonly leads: readonly LeadRecord[];
  readonly now: Date;
  readonly days: number;
  readonly screen: ClaimScreen;
  readonly shopTimeZone?: string | null;
}): OccasionCalendar {
  // The shop's date of now, as the planner reads it: a touch from a later date is still ahead.
  const today = shopCalendarDay(input.now, input.shopTimeZone);
  const rows: OccasionCalendarRow[] = [];
  const unplanned: OccasionCalendarUnplanned[] = [];
  const held: OccasionCalendarHeld[] = [];
  const suppressed: { contactPseudonym: string; cites: readonly LeadCitation[]; note: string }[] = [];
  const excludedLost: string[] = [];
  let contacts = 0;
  let elapsed = 0;
  let memoryExcludedMalformed = 0;
  let memoryQuarantined = 0;

  for (const records of groupByContact(input.leads).values()) {
    const first = records[0];
    if (first === undefined) continue;
    contacts += 1;
    const card = cardFor(first, input.now, input.screen);
    memoryExcludedMalformed += card.excludedMalformed;
    memoryQuarantined += card.quarantined.length;
    const token = first.contactPseudonym;

    const open = records.some((r) => stageFrom(r, cardFor(r, input.now, input.screen)).open);
    if (!open) {
      excludedLost.push(token);
      continue;
    }

    const plan = planOccasions(occasionFactsFor(records, card), input.now, input.days, input.shopTimeZone);
    if (plan.suppressed !== null) {
      suppressed.push({ contactPseudonym: token, cites: plan.suppressed.cites, note: plan.suppressed.note });
      continue;
    }
    elapsed += plan.elapsed;
    for (const u of plan.unplanned) unplanned.push({ ...u, contactPseudonym: token });
    for (const h of plan.held) held.push({ ...h, contactPseudonym: token });
    for (const touch of plan.touches) {
      // A touch already open is read as of now. One still ahead is read at the END of its first day, the least
      // permissive instant of it (a window only closes as time passes): a window that shuts at midday on the
      // due day must not read open because midnight was. G19: the day is the shop's (shopDayEnd), and "ahead"
      // is a date after the shop's date of now, so a touch open today in the shop is read now whatever UTC says.
      const routeAt = touch.from.getTime() > today.getTime() ? shopDayEnd(touch.from, input.shopTimeZone) : input.now;
      const reading = routeReadingAt(records, routeAt);
      if (reading === null) continue;
      rows.push({ contactPseudonym: token, touch, route: reading.route, routeAt, routeThreadId: reading.threadId });
    }
  }

  const byToken = (a: { contactPseudonym: string }, b: { contactPseudonym: string }): number => a.contactPseudonym.localeCompare(b.contactPseudonym);
  rows.sort((a, b) => a.touch.from.getTime() - b.touch.from.getTime() || byToken(a, b) || a.touch.id.localeCompare(b.touch.id));
  unplanned.sort((a, b) => byToken(a, b) || a.id.localeCompare(b.id) || a.reason.localeCompare(b.reason));
  held.sort((a, b) => byToken(a, b) || a.id.localeCompare(b.id));
  suppressed.sort(byToken);
  excludedLost.sort((a, b) => a.localeCompare(b));
  return {
    generatedAt: iso(input.now),
    days: input.days,
    rows,
    unplanned,
    held,
    suppressed,
    excludedLost,
    seasonal: seasonalWindows(input.now, input.days, input.shopTimeZone),
    contacts,
    elapsed,
    memoryExcludedMalformed,
    memoryQuarantined,
  };
}
